import {
    Text,
    Container,
    FederatedPointerEvent,
    FillStyle,
    Graphics,
    PointData,
    TextStyle,
    Rectangle,
    Point,
    IRenderLayer,
} from "pixi.js";
import { FontName } from "./logic";


export function makeZoomInOut(container: Container, eventReceiver: Container | null = null, onZoom?: (scale: number) => void) {
    const receiver = eventReceiver || container.parent!;
    receiver.eventMode = "static";
    receiver.cursor = "zoom-in";
    const minScale = 0.4;
    const maxScale = 2;
    const zoomSpeed = 0.1;

    receiver.on("wheel", (e: WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
        const newScale = Math.min(maxScale, Math.max(minScale, container.scale.x + delta));

        const mousePosition = receiver.toLocal(new Point(e.clientX, e.clientY));
        const newContainerPosition = new Point(
            mousePosition.x - (mousePosition.x - container.position.x) * (newScale / container.scale.x),
            mousePosition.y - (mousePosition.y - container.position.y) * (newScale / container.scale.y)
        );

        container.scale.set(newScale);
        container.position.set(newContainerPosition.x, newContainerPosition.y);

        onZoom?.(newScale);
    });
}

export const backgroundColor = 0x202040;

/**
 * Adds a background for the specified parent. The background is added to the container argument.
 * NOTE: the background is added as the first child of the container (index 0) !!!
 * @param container 
 * @param parent 
 * @param backColor 
 * @param alpha 
 */
export function addBackground(container: Container, parent: Text | Container, backColor: number = backgroundColor, alpha: number = 0.9) {
    const back = container.getChildByLabel("background");
    if(back) container.removeChild(back);

    const xOffset = parent instanceof Text ? parent.anchor.x * -parent.width : 0;

    const background = new Graphics()
        .rect(-5 + xOffset, -2, parent.width + 10, parent.height + 10)
        .fill({ color: backColor, alpha: alpha });
    background.label = "background";
    container.addChildAt(background, 0);
    return background;
}

/**
 * Adds a tooltip to the specified container.
 * @param host container to which the tooltip will be added
 * @param target interactive element (e.g. sprite) that will trigger the tooltip
 * @param text tooltip text
 * @param offset optional offset from the target container
 * @param textStyle optional style for the tooltip text
 * @param backgroundStyle optional background style for the tooltips background. Default is black with alpha 1
 * @param isCentered if true, the tooltip will be centered horizontally. Default is true
 * @returns the Text instance with the tooltip text.
 */
export function addTooltip(
    host: Container,
    target: Container,
    text: string,
    offset: PointData | null | undefined = { x: 0, y: 0 },
    textStyle?: Partial<TextStyle>,
    backgroundStyle?: Partial<FillStyle>,
    isCentered = true,
    layer?: IRenderLayer
): Text {
    //  styles for text and background
    const defaultTextStyle = {
        fontFamily: FontName.Tooltip,
        fontSize: 18,
        fill: 0xffff7f
    };
    const defaultBgStyle = { color: backgroundColor, alpha: 1};
    const txtStyle = { ...defaultTextStyle, ...textStyle } as Partial<TextStyle>;
    const backStyle = { ...defaultBgStyle, ...backgroundStyle };

    const tooltipText = new Text({
        text: text,
        style: txtStyle,
    });
    tooltipText.visible = false;
    tooltipText.label = "tooltipText";

    const tooltipBg = new Graphics();
    tooltipBg.visible = false;
    tooltipBg.label = "tooltipBg";

    const updateText = (text: string) => {
        tooltipText.text = text;
        const height = tooltipText.height + 10;
        const width = tooltipText.width + 20;
        tooltipText.x = isCentered ? (target.width - width) / 2 + 10 : 10;
        tooltipText.y = 2;

        tooltipBg.clear();
        tooltipBg.rect(0, 0, width, height).stroke({ color: 0xafaf70, width: 1 }).fill(backStyle);
        //tooltipBg.roundRect(0, 0, width, height, 5).fill(backStyle);
        tooltipBg.x = isCentered ? (target.width - width) / 2 : 0;
    }

    offset = offset || { x: 0, y: 0 };
    const tooltipContainer = new Container();
    tooltipContainer.position.set(target.x + offset.x, target.y + offset.y);
    tooltipContainer.label = "tooltipContainer";
    tooltipContainer.addChild(tooltipBg);
    tooltipContainer.addChild(tooltipText);
    host.addChild(tooltipContainer);

    target.eventMode = "static";
    target.on("pointerover", () => {
        tooltipText.visible = true;
        tooltipBg.visible = true;
        updateText(tooltipText.text);
    });
    target.on("pointerout", () => {
        tooltipText.visible = false;
        tooltipBg.visible = false;
    });

    if(layer) {
        layer.attach(tooltipContainer);
    }
    return tooltipText;
}

class DragData {
    public isDragging: boolean = false;
    public pointerStartPosition: PointData = { x: 0, y: 0 };
    public objectStartPosition: PointData = { x: 0, y: 0 };
}

/**
 * Makes the given container draggable.
 * Note: the eventReceiver or containers parent will be set to 'static' event mode and receive pointer events.
 * @param draggable the container to make draggable
 * @param eventReceiver optional container to receive the pointer events. If not set, the draggable containers parent will be used.
 * @param bounds optional bounds for the draggable container. The x and y are minimum values, while width and height are maximum values.
 */
export function makeDraggable(
    draggable: Container,
    eventReceiver: Container | null = null,
    bounds?: Rectangle,
) {
    const receiver = eventReceiver || draggable.parent!;
    receiver.eventMode = "static";
    receiver.cursor = "grab";
    const dragData: DragData = new DragData();

    receiver.off("pointerdown").off("pointermove").off("pointerup").off("pointerupoutside");

    receiver.on("pointerdown", (e: FederatedPointerEvent) => {
        if (!dragData.isDragging) {
            dragData.pointerStartPosition = receiver.toLocal(e.global);
            dragData.objectStartPosition = draggable.position.clone();
            dragData.isDragging = true;
            receiver.cursor = "move";
        }
    });

    receiver.on("pointermove", (e: FederatedPointerEvent) => {
        if (dragData && dragData.isDragging) {
            const pointerPosition = receiver.toLocal(e.global);
            let newX = dragData.objectStartPosition.x + pointerPosition.x - dragData.pointerStartPosition.x;
            let newY = dragData.objectStartPosition.y + pointerPosition.y - dragData.pointerStartPosition.y;
            if (bounds) {
                if (newX > bounds.width)
                    newX = bounds.width;
                if (newY > bounds.height)
                    newY = bounds.height;
                if (newX < bounds.x)
                    newX = bounds.x;
                if (newY < bounds.y)
                    newY = bounds.y;
            }
            draggable.position.set(newX, newY);
        }
    });

    receiver.on("pointerup", () => {
        dragData.isDragging = false;
        receiver.cursor = "grab";
    });

    receiver.on("pointerupoutside", () => {
        dragData.isDragging = false;
        receiver.cursor = "grab";
    });
}

/**
 * Attaches a double click handler to the given container.
 * The containers eventMode is set to 'static'
 * @param container
 * @param handler
 * @param context
 */
export function attachDblClick(
    container: Container,
    handler: (event: FederatedPointerEvent) => void,
    context?: any,
) {
    container.on("pointerdown", (e: FederatedPointerEvent) => {
        let delay = Date.now() - lastTime;
        lastTime = Date.now();
        if (delay < 350) {
            handler(e);
        }
    }, context);
    container.eventMode = "static";
}
let lastTime = 0;

/**
 * Formats the given time in seconds as 'Nd Nh Nm Ns'.
 * @param seconds
 * @returns string formatted as number of days, hours, minutes and seconds
 */
export function formatSeconds(seconds: number) {
    if (seconds === 0) return '0s';    
    seconds = Math.floor(seconds);

    let d = 0, h = 0, m = 0;
    if (seconds >= 86400) {
        d = Math.floor(seconds / 86400);
        seconds = seconds % 86400;
    }
    if (seconds >= 3600) {
        h = Math.floor(seconds / 3600);
        seconds = seconds % 3600;
    }
    if (seconds >= 60) {
        m = Math.floor(seconds / 60);
        seconds = seconds % 60;
    }
    let result = "";
    if (d > 0) {
        result += `${d.toFixed(0)}d `;
    }
    if (h > 0) {
        result += `${h.toFixed(0)}h `;
    }
    if (m > 0) {
        result += `${m.toFixed(0)}m `;
    }
    if (seconds > 0 || result === "") {
        result += `${seconds.toFixed(0)}s`;
    }
    return result;
}

/**
 * Formats the given number with suffixes (k, M etc.)
 * @param value the number to format
 * @param decimals number of decimal places
 * @returns 
 */
export function formatNumber(value: number | undefined, decimals: number = 0) {
    value = value || 0;
    return value > 999999 ? (value / 1000000).toFixed(decimals) + "M" :
        value > 999 ? (value / 1000).toFixed(decimals) + "k" : value.toFixed(0);
}

/**
 * Returns the given number formatted with digit grouping.
 * @param value 
 * @returns 
 */
export function formatNumberWithDigitGrouping(value: number): string {
    return new Intl.NumberFormat(navigator.language).format(value);
}

/**
 * Uses adaptive decimal places in order to have the precision high enough that the difference between two numbers is visible.
 * e.g. if having numbers 2330 and 2300, the formatNumber() would display both as '2.3k', while formatPairAdaptive() would display them as '2.33k' and '2.30k'. 
 * @param a 
 * @param b 
 * @param min 
 * @param max 
 * @returns 
 */
export function formatPairAdaptive(a: number, b: number, min = 1, max = 3): [string, string] {
  const absMax = Math.max(Math.abs(a), Math.abs(b));
  const [scale, suffix] =
    absMax >= 1_000_000 ? [1_000_000, "M"] :
    absMax >= 1_000     ? [1_000, "k"] :
                          [1, ""];
  const sa = a / scale, sb = b / scale;

  let d = suffix ? min : 0; // no decimals for < 1k to match your current behavior
  while (suffix && d < max && sa.toFixed(d) === sb.toFixed(d)) d++;

  const fmt = (x: number) => suffix ? x.toFixed(d) + suffix : x.toFixed(0);
  return [fmt(sa), fmt(sb)];
}

function deepEqualInternal(
    object1: any,
    object2: any,
    visited: Set<any> = new Set(),
): boolean {
    if (object1 === null || object2 === null) return object1 === object2;
    
    // Handle arrays separately
    if (Array.isArray(object1) && Array.isArray(object2)) {
        if (object1.length !== object2.length) return false;
        for (let i = 0; i < object1.length; i++) {
            if (!deepEqualInternal(object1[i], object2[i], visited)) return false;
        }
        return true;
    }

    // Handle Map type comparison
    if (object1 instanceof Map && object2 instanceof Map) {
        if (object1.size !== object2.size) return false;
        for (const [key, value] of object1) {
            if (!object2.has(key) || !deepEqualInternal(value, object2.get(key), visited)) {
                return false;
            }
        }
        return true;
    }


    // Handle object type comparison
    if (typeof object1 === "object" && typeof object2 === "object") {
        if (visited.has(object1) || visited.has(object2)) return true; // circular reference check
        visited.add(object1);
        visited.add(object2);

        const keys1 = Object.keys(object1);
        const keys2 = Object.keys(object2);
        if (keys1.length !== keys2.length) return false;

        for (const key of keys1) {
            if (
                !keys2.includes(key) ||
                !deepEqualInternal(object1[key], object2[key], visited)
            ) {
                return false;
            }
        }
        return true;
    }

    // Handle primitive types
    return object1 === object2;
}

/**
 * Compares two objects for property equality.
 * @param object1
 * @param object2
 * @returns
 */
export function deepEqual(object1: any, object2: any): boolean {
    // Check for reference equality
    if (object1 === object2) return true;

    // Handle null and undefined cases
    if (object1 == null || object2 == null) return false;

    // Check if both values are of the same type
    if (typeof object1 !== typeof object2) return false;

    return deepEqualInternal(object1, object2);
}

function isObject(object: any) {
    return object != null && typeof object === "object";
}


/**
 *   Simple keyboard mapper.
 */
class KeyboardMapper {
    /**
     *   Stores keyboard pressed state.
     */
    private keyboard: Map<string, boolean>;

    /**
     *   Stores an array of KeyboardAction instances per Global.State. The 'state' indexer is a numeric value from the Global.State enum.
     */
    private stateActions: { [state: string]: KeyboardAction[] };

    private ALT_KEY: string = "Alt";
    private SHIFT_KEY: string = "Shift";
    private CTRL_KEY: string = "Control";

    /**
     *   Creates a new KeyboardMapper instance.
     */
    constructor() {
        this.stateActions = {};
        this.keyboard = new Map<string, boolean>();

        document.addEventListener('keydown', this.keydown.bind(this), false);
        document.addEventListener('keyup', this.keyup.bind(this), false);
    }

    /**
     * Invokes needed action handlers based on the pressed keys.
     * @remarks Only handlers assigned to the given state are processed
     * @param currentState - the game state for which handlers are processed e.g. scene name or empty string for global actions
     * */
    public update(currentState: string) {
        //  state specific handler
        let actions: KeyboardAction[] = this.stateActions[currentState];
        this.findHandlerAndInvoke(actions);

        //  global handlers
        actions = this.stateActions[""];
        this.findHandlerAndInvoke(actions);
    }

    /**
     * Adds an action handler scoped to the given state.
     * @remarks The handler is invoked only if the `state` matches the current state passed to the `update()` method.
     * @param action the KeyboardAction instance to add.
     * @param state the game state string for which the action is executed e.g. scene name or empty string for global actions
     */
    public addKeyboardActionHandler(action: KeyboardAction, state: string) {
        if (!this.stateActions[state]) {
            this.stateActions[state] = [];
        }
        this.stateActions[state].push(action);
    };

    /**
     * Checks if the given action is defined for the given state.
     * @param action 
     * @param state the game state for which the action is executed e.g. scene name or empty string for global actions
     * @returns 
     */
    public isActionDefined(action: KeyboardAction, state: string): boolean {
        return this.stateActions[state]?.find(a => a.key === action.key) !== undefined;
    }

    /** Checks if a key is pressed */
    public isKeyDown(code: string) {
        return this.keyboard.get(code);
    }

    /**
     *   Searches for all keyboard handlers with matching current pressed key combinations and invokes them.
     */
    private findHandlerAndInvoke(actions: KeyboardAction[]) {
        if (actions) {
            const len = actions.length;
            for (let i: number = 0; i < len; i++) {
                const ka = actions[i];
                const isMatchingAlt = ka.altKey ? this.keyboard.get(this.ALT_KEY) === ka.altKey : !this.keyboard.get(this.ALT_KEY);
                const isMatchingShift = ka.shiftKey ? this.keyboard.get(this.SHIFT_KEY) === ka.shiftKey : !this.keyboard.get(this.SHIFT_KEY);
                const isMatchingCtrl = ka.ctrlKey ? !this.keyboard.get(this.CTRL_KEY) === ka.ctrlKey : !this.keyboard.get(this.CTRL_KEY);
                if (
                    ka &&
                    ka.handler &&
                    this.keyboard.get(ka.key) &&
                    isMatchingAlt &&
                    isMatchingShift &&
                    isMatchingCtrl
                ) {
                    ka.handler();
                    if (ka.releaseKeyAfterInvoke) {
                        this.keyboard.set(ka.key, false);
                    }
                }
            }
        }
    }

    private keydown(e: KeyboardEvent) {
        this.keyboard.set(e.key, true);
    }

    private keyup(e: KeyboardEvent) {
        this.keyboard.set(e.key, false);
    }
}

export const keyboardMapper = new KeyboardMapper();

export type KeyboardActionCallback = () => void;

export class KeyboardAction {
    /**
     *   Creates a new KeyboardAction instance.
     */
    constructor(
        /**
         *  The key code (printable character representation) for the action.
         */
        public key: string,
        /**
         * The name of the action. Used for debugging or UX purposes.
         */
        public name: string,
        /**
         * The handler to be invoked when the key is pressed.
         */
        public handler?: KeyboardActionCallback,
        public releaseKeyAfterInvoke: boolean = true,
        public shiftKey: boolean = false,
        public ctrlKey: boolean = false,
        public altKey: boolean = false,
    ) { }

    /**
     *   Returns true if the handler is assigned.
     */
    public isAssigned(): boolean {
        return this.handler !== undefined;
    }
}